import DashboardSidebar from "./components/DashboardSidebar";
import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Changelog from "@/pages/Changelog";
import Settings from "@/pages/Settings";
import AuthPage from "@/pages/AuthPage";
import ProtectedRoute from "./components/ProtectedRoute";
import WorkspacesHeader from "./components/Reports/WorkspacesHeader";
import { useEffect } from "react";
import useUserData from "./hooks/useUserData";
import { getCookie } from "./lib/cookies";
import SubscriptionModal from "./components/SubscriptionModal";
import useSubscriptionModal from "./hooks/useSubscriptionModal";
import { AnimatePresence } from "motion/react";
import { API_URL } from "./env";
import { Toaster } from "react-hot-toast";
import CompleteSignup from "./pages/CompleteSignup";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OauthSuccess from "@/pages/OAuthSuccess";
import MediaViewerContainer from "@/components/Reports/MediaViewerContainer";
import Reports from "./pages/Reports";
import NotFound from "./pages/NotFound";
import NoWorkspaceFound from "./pages/NoWorkspaceFound";
import { useWorkspaceStore } from "./hooks/useWorkspace";
import { useIntegrationsStore } from "./hooks/useIntegrations";
import AcceptInvitationPage from "./components/Reports/AcceptInvitationPage";
import { syncLoginToExtension } from "@/utils/extensionSync";
import FeedbackPage from "./pages/FeedbackPage";
import Plans from "./pages/Plans";
import RecordLink from "./pages/RecordLink";
import Onboarding from "./pages/Onboarding";

interface LoginData {
  token: string;
  userId: string;
  userData?: any;
}

function App() {
  const location = useLocation();
  const { setUser, user } = useUserData();
  const { isOpen, open } = useSubscriptionModal();
  const { setCurrentWorkspace, setWorkspaces, currentWorkspace, workspaces } =
    useWorkspaceStore();
  const { checkJiraStatus } = useIntegrationsStore();
  const navigate = useNavigate();
  const params = useParams();

  const token = getCookie("token");

  const getUser = async () => {
    try {
      if (token) {
        const res = await fetch(`${API_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const user = await res.json();

        if (user) {
          setUser({ ...user });

          // ✅ Sync login to extension
          syncLoginToExtension({
            access_token: token,
            userId: user._id,
          })
            .then((result: any) => {
              // if (result.success) {
              //   console.log("✅ Extension synced successfully");
              // } else {
              //   console.log(
              //     "Extension not available (user may not have it installed)"
              //   );
              // }
            })
            .catch((err) => {
              // console.log("Extension sync failed:", err);
            });
        }
      }
    } catch (err) {
      // if (token) {
      //   deleteCookie("token");
      // }

      navigate("/auth");
    }
  };

  // ✅ Logout handler with extension sync
  // const handleLogout = async () => {
  //   // Sync logout to extension
  //   await syncLogoutToExtension();

  //   // Your existing logout logic would go here
  //   deleteCookie("token");
  //   setUser(null);
  //   navigate("/auth");
  // };

  useEffect(() => {
    if (user?.workspaces) {
      setWorkspaces(user.workspaces);
    }
  }, [user]);

  useEffect(() => {
    if (currentWorkspace) {
      checkJiraStatus(currentWorkspace._id);
    }
  }, [currentWorkspace, checkJiraStatus]);

  useEffect(() => {
    getUser();
  }, [token]);

  // Update page title only when page is visible and navigation is complete
  useEffect(() => {
    const pathname = location.pathname;

    // Skip if on media page (handled by MediaViewerContainer)
    if (pathname.startsWith("/media")) {
      return;
    }

    // Wait for navigation to fully complete using requestIdleCallback or timeout
    const timer = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Only update if we're still on the same pathname
        if (location.pathname !== pathname) {
          return;
        }

        // Set title based on current route
        if (pathname.startsWith("/dashboard")) {
          document.title = "Toolbar";
        }
        // else if (pathname === '/settings') {
        //   document.title = 'Toolbar | Settings';
        // } else if (pathname === '/changelog') {
        //   document.title = 'Toolbar | Changelog';
        // } else if (pathname === '/plans') {
        //   document.title = 'Toolbar | Plans';
        // }
        // else {
        //   document.title = "Toolbar";
        // }
      });
    });

    return () => cancelAnimationFrame(timer);
  }, [location.pathname, location.key]);

  const showUI =
    location.pathname !== "/auth" &&
    location.pathname !== "/404" &&
    location.pathname !== "/playground" &&
    !location.pathname.includes("/media") &&
    !location.pathname.startsWith("/record/") &&
    location.pathname !== "/reset-password" &&
    location.pathname !== "/onboarding";

  return (
    <>
      <AnimatePresence>{isOpen && <SubscriptionModal />}</AnimatePresence>
      {/* {width < 1024 && location.pathname !== "/auth" && <Beta />} */}
      <Toaster
        toastOptions={{ duration: 2000 }}
        containerStyle={{ zIndex: 10000000000 }}
        position="top-center"
      />
      {showUI && <WorkspacesHeader />}
      <div
        className={`${
          location.pathname !== "/404" &&
          location.pathname !== "/playground" &&
          !location.pathname.includes("/media")
            ? "px-4"
            : ""
        } onest ${showUI ? "pt-[64px] flex gap-3" : ""} relative`}
      >
        {showUI &&
          !location.pathname.startsWith("/feedback") &&
          currentWorkspace?.users
            .map((user) => user._id)
            .includes(user?._id) && <DashboardSidebar />}

        <Routes>
          <Route path="/" element={<NoWorkspaceFound />} />
          <Route path="/dashboard" element={<NoWorkspaceFound />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
          {/* <Route path="/dashboard" element={<Reports />} /> */}
          <Route
            path="/dashboard/:workspaceId"
            element={
              // <ProtectedRoute>
              <Reports />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          {/* <Route
            path="/inbox"
            element={
              <ProtectedRoute>
                <InboxPage />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path="/changelog"
            element={
              <ProtectedRoute>
                <Changelog />
              </ProtectedRoute>
            }
          />
          <Route path="/media" element={<MediaViewerContainer />} />
          <Route path="/auth/success" element={<OauthSuccess />} />
          <Route path="/complete-signup" element={<CompleteSignup />} />
          {/* <Route path="/callback" element={<GithubCallback />} /> */}
          {/* <Route path="/playground" element={<Playground />} /> */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/record/:token" element={<RecordLink />} />
          {/* <Route path="/404" element={<NotFound />} /> */}
          <Route path="/onboarding" element={<Onboarding />} />

          <Route
            path="*"
            element={
              <ProtectedRoute>
                <NotFound />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </>
  );
}

export default App;
